import { BrokerModel, ProfileModel, swapElementsPositions } from "dash-common";
import { useContext, useEffect, useMemo, useRef } from "react";
import {
  Button,
  DashPlaceholder,
  Icon,
  Text,
  Image,
  useScreenResolution,
  ModalContext,
  Modal,
  PaginationContext,
  DefaultPaginationValues,
  Spinner,
  Flex,
  localStorage,
  ViewerContext,
} from "dash-ui";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import {
  useGetBrokerByIdQuery,
  useGetPortfoliosByPublicFolderQuery,
} from "gql/generated.graphql";
import ViewAllPortfolioPropertiesModal from "containers/portfolio/ViewAllPortfolioPropertiesModal/ViewAllPortfolioPropertiesModal";
import { getPreviewPortfolioPropertyLink } from "common/helpers/portfolio";

import {
  ButtonsContainer,
  ContainerWrapper,
  GridWrapper,
  PropertyItemOverlay,
  PropertyWrapper,
  Wrapper,
} from "./PortfolioPropertyPreviewSidebar.styles";
import BrokerInfoCard from "./components/BrokerInfoCard/BrokerInfoCard";
import BrokerAvailabilityModal from "../AvailabilityModal/AvailabilityModal";
import { AgentContactCard } from "v2/components/agent-contact-card";
import { BrokerModal } from "v2/components/broker-modal";
import { AgentContactModal } from "v2/components/agent-contact-modal";

const PortfolioPropertyPreviewSidebar = ({
  portfolioFolderId,
  currentPropertyId,
  withBreadcrumb,
  brokerId,
  portfolioId,
}: {
  portfolioFolderId?: string;
  currentPropertyId?: string;
  brokerId?: string;
  withBreadcrumb?: boolean;
  portfolioId?: string;
}) => {
  // HTML Element reference for "contact me" functionality on broker modal
  const contactFormRef = useRef<HTMLInputElement | null>(null);

  const theme = useTheme();
  const { t } = useTranslation(["pages", "common"]);
  const { toggleModal, isOpenCurrent } = useContext(ModalContext);
  const { setTotal, getRefetchProps } = useContext(PaginationContext);
  const { broker: viewerBroker } = useContext(ViewerContext);
  const {
    devices: { isTablet, isMobile },
  } = useScreenResolution();
  const isOwnPortfolio = viewerBroker?._id === brokerId;
  const { data } = useGetBrokerByIdQuery({
    variables: { brokerId },
    skip: !brokerId,
  });
  const {
    data: portfolioFolderItems,
    loading,
    fetchMore,
  } = useGetPortfoliosByPublicFolderQuery({
    variables: {
      portfolioFolderId,
      pagination: getRefetchProps(DefaultPaginationValues),
      password: localStorage.getPortfolioPassword(),
    },
    skip: !portfolioFolderId,
  });
  const { data: allPortfolioFolderItems } = useGetPortfoliosByPublicFolderQuery(
    {
      variables: {
        portfolioFolderId,
        pagination: {
          allowLimit: false,
        },
        password: localStorage.getPortfolioPassword(),
      },
      skip: !portfolioFolderId,
    }
  );
  const broker = data?.getBrokerById;

  const onClickAvailabilityButton = () => {
    if (broker) {
      toggleModal(true, "BrokerAvailabilityModal");
    }
  };

  const { portfolioProperties } = useMemo(
    () => ({
      portfolioProperties:
        portfolioFolderItems?.getPortfoliosByPublicFolder.collection || [],
      totalCount:
        portfolioFolderItems?.getPortfoliosByPublicFolder.totalCount || 0,
    }),
    [portfolioFolderItems]
  );

  const { allPortfolioProperties, totalCount } = useMemo(
    () => ({
      allPortfolioProperties:
        allPortfolioFolderItems?.getPortfoliosByPublicFolder.collection || [],
      totalCount:
        allPortfolioFolderItems?.getPortfoliosByPublicFolder.totalCount || 0,
    }),
    [allPortfolioFolderItems]
  );

  const propertiesSlice = useMemo(() => {
    if (!allPortfolioProperties.length) return [];
    const index = allPortfolioProperties.findIndex(
      (item) => item.property?._id === currentPropertyId
    );

    return swapElementsPositions(allPortfolioProperties, index, 0).slice(0, 4);
  }, [allPortfolioProperties, currentPropertyId]);

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    setTotal(totalCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount]);

  return (
    <ContainerWrapper ml={[0, 6, 6, 6]} withBreadcrumb={withBreadcrumb}>
      {loading && (
        <Flex alignItems="center" justifyContent="center" w="100%">
          <Spinner size={24} />
        </Flex>
      )}
      {broker && (
        <BrokerModal
          broker={broker}
          properties={portfolioProperties}
          propertiesTotal={totalCount}
          isLoading={loading}
          loadMore={{
            fetchMoreProps: {
              fetchMore,
              variables: { portfolioFolderId },
              queryName: "getPortfoliosByPublicFolder",
            },
          }}
          contactFormRef={contactFormRef}
        />
      )}
      {broker && <BrokerAvailabilityModal broker={broker} />}
      {isMobile && broker && (
        <AgentContactModal
          getBrokerQuery={data}
          getPortfolioQuery={allPortfolioFolderItems}
          propertyId={currentPropertyId}
        />
      )}
      {isMobile && (
        <ButtonsContainer w="100%" p={4}>
          <Button
            onClick={() => {
              toggleModal(true, "ViewAllPortfolioPropertiesModal");
            }}
            w="100%"
          >
            {t("pages:portfoliosPage.viewPropertyPortfolio")}
          </Button>
          <Button
            ml={2}
            variation="outline"
            w={100}
            onClick={() => toggleModal(true, "BrokerInfoModal")}
          >
            {t("common:labels.more")}
          </Button>
        </ButtonsContainer>
      )}
      {portfolioProperties.length > 0 && !isMobile && (
        <Wrapper p={4} w={[0, 215, 255, 255]}>
          <Text color={theme.colors.n90} semibold>
            {t("propertyPage.propertyPortfolio", {
              amount: totalCount,
            })}
          </Text>
          <GridWrapper mt={3} mb={4}>
            {propertiesSlice.map((item) => (
              <PropertyWrapper
                style={{
                  borderRadius: "7px",
                  border:
                    item.property?._id === currentPropertyId
                      ? "2px solid rgba(27, 117, 187, 1)"
                      : "none",
                }}
                key={item.property?._id}
                h={[0, 89, 110, 110]}
                onClick={(e) => {
                  if (e && e.stopPropagation) e.stopPropagation();
                  window?.open(
                    getPreviewPortfolioPropertyLink(
                      { ...item, name: item.property?.name },
                      broker as BrokerModel,
                      broker?.profile as ProfileModel
                    ),
                    "_blank"
                  );
                }}
              >
                {item.property?.mainPropertyMediaFile ? (
                  <Image
                    src={
                      item.property.mainPropertyMediaFile.files[0].thumbnailFile
                        ?.publicUrl || ""
                    }
                    size={isTablet ? 89 : 106}
                  />
                ) : (
                  <DashPlaceholder size={isTablet ? 89 : 106} />
                )}
                <PropertyItemOverlay
                  alignItems="center"
                  justifyContent="center"
                  flexDirection="column"
                  px={4}
                >
                  <Icon name="show" color={theme.colors.white} />
                  <Text sm semibold color={theme.colors.white}>
                    {item.property?.name}
                  </Text>
                </PropertyItemOverlay>
              </PropertyWrapper>
            ))}
          </GridWrapper>
          {totalCount > 4 && (
            <Button
              minWidth={[0, 183, 223, 223]}
              w="100%"
              variation="outline"
              h={40}
              onClick={() =>
                toggleModal(true, "ViewAllPortfolioPropertiesModal")
              }
            >
              {t("pages:propertyPage.viewAll")}
            </Button>
          )}
        </Wrapper>
      )}
      {!isMobile && broker && (
        <AgentContactCard
          getBrokerQuery={data}
          onCheckAvailability={onClickAvailabilityButton}
          propertyId={currentPropertyId}
          getPortfolioQuery={allPortfolioFolderItems}
          contactFormRef={contactFormRef}
        />
      )}
    </ContainerWrapper>
  );
};

export default PortfolioPropertyPreviewSidebar;
